import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Cookie, Shield, Settings, X, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'asceticlife_cookie_consent';

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    functional: true,
    analytics: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      functional: true,
      analytics: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleAcceptSelected = () => {
    const consent = {
      ...preferences,
      necessary: true, // Always required
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const consent = {
      necessary: true,
      functional: false,
      analytics: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
      >
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            {/* Main Banner */}
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-[#006039] to-emerald-600 text-white flex-shrink-0">
                  <Cookie className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                    🍪 Wir respektieren Deine Privatsphäre
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                    Wir verwenden lokale Speicherung (localStorage) und Cookies, um Dein Erlebnis zu verbessern. 
                    Du kannst wählen, welche Daten gespeichert werden dürfen.
                  </p>

                  {/* Cookie Details (expandable) */}
                  <AnimatePresence>
                    {showDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="mb-4 space-y-3"
                      >
                        {/* Necessary Cookies */}
                        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Shield className="h-4 w-4 text-[#006039]" />
                              <span className="font-medium text-sm">Notwendig</span>
                              <span className="text-xs text-slate-500">(Immer aktiv)</span>
                            </div>
                            <div className="w-10 h-5 bg-[#006039] rounded-full flex items-center justify-end px-0.5">
                              <div className="w-4 h-4 bg-white rounded-full" />
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Login-Status, Spracheinstellungen, Sicherheitstoken
                          </p>
                        </div>

                        {/* Functional Cookies */}
                        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Settings className="h-4 w-4 text-blue-500" />
                              <span className="font-medium text-sm">Funktional</span>
                            </div>
                            <button
                              onClick={() => setPreferences(p => ({ ...p, functional: !p.functional }))}
                              className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${
                                preferences.functional ? 'bg-[#006039] justify-end' : 'bg-slate-300 justify-start'
                              }`}
                            >
                              <div className="w-4 h-4 bg-white rounded-full shadow" />
                            </button>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Spielstände, Benutzereinstellungen, Theme-Präferenzen
                          </p>
                        </div>

                        {/* Analytics Cookies */}
                        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <svg className="h-4 w-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              <span className="font-medium text-sm">Analyse</span>
                            </div>
                            <button
                              onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                              className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${
                                preferences.analytics ? 'bg-[#006039] justify-end' : 'bg-slate-300 justify-start'
                              }`}
                            >
                              <div className="w-4 h-4 bg-white rounded-full shadow" />
                            </button>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Anonyme Nutzungsstatistiken zur Verbesserung der App
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Buttons */}
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      onClick={handleAcceptAll}
                      className="bg-gradient-to-r from-[#006039] to-emerald-600 hover:from-[#00915a] hover:to-emerald-500"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Alle akzeptieren
                    </Button>
                    
                    {showDetails ? (
                      <Button
                        variant="outline"
                        onClick={handleAcceptSelected}
                      >
                        Auswahl speichern
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => setShowDetails(true)}
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Einstellungen
                      </Button>
                    )}
                    
                    <Button
                      variant="ghost"
                      onClick={handleRejectAll}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      Nur notwendige
                    </Button>
                  </div>
                </div>

                {/* Close Button */}
                <button
                  onClick={handleRejectAll}
                  className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Footer Link */}
            <div className="px-6 py-3 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500">
                Mehr Informationen findest Du in unserer{' '}
                <Link to="/privacy" className="text-[#006039] hover:underline">Datenschutzerklärung</Link>.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Hook to check cookie consent status
export const useCookieConsent = () => {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        setConsent(JSON.parse(stored));
      } catch (e) {
        setConsent(null);
      }
    }
  }, []);

  return consent;
};

// Function to reset cookie consent (for settings page)
export const resetCookieConsent = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  window.location.reload();
};

export default CookieBanner;
